import { useEffect } from "react";
import { CalendarX2, Star, Tag, X } from "lucide-react";

import { useCategoriesQuery } from "@/hooks/useCategoriesQuery";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getCategoryColors } from "@/lib/outlookCategoryColors";
import { cn } from "@/lib/utils";
import type { CalendarFilters } from "@/components/calendar/types";

interface CalendarFilterBarProps {
  filters: CalendarFilters;
  onChange: (patch: Partial<CalendarFilters>) => void;
  onClear: () => void;
  isDefault: boolean;
}

const PRIORITY_STARS = [1, 2, 3] as const;

export function CalendarFilterBar({
  filters,
  onChange,
  onClear,
  isDefault,
}: CalendarFilterBarProps) {
  const { data: categories, isLoading: categoriesLoading } = useCategoriesQuery();

  const selectedCategories = new Set<string>(filters.categoryId ?? []);

  // Drop category ids that no longer exist (e.g. the category was deleted)
  // so a stale filter never silently matches zero events.
  useEffect(() => {
    if (categoriesLoading || !categories || !filters.categoryId) return;
    const validIds = new Set(categories.items.map((c) => c.id));
    const cleaned = filters.categoryId.filter((id) => validIds.has(id));
    if (cleaned.length !== filters.categoryId.length) {
      onChange({ categoryId: cleaned.length > 0 ? cleaned : undefined });
    }
  }, [categories, categoriesLoading, filters.categoryId, onChange]);

  function toggleCategory(id: string) {
    const next = new Set(selectedCategories);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ categoryId: next.size > 0 ? Array.from(next) : undefined });
  }

  function toggleUncategorized() {
    onChange({ uncategorized: filters.uncategorized ? undefined : true });
  }

  function handlePriority(star: 1 | 2 | 3) {
    onChange({ priority: filters.priority === star ? undefined : star });
  }

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border bg-card px-5 py-3 shadow-sm">
      {/* Category */}
      <div className="flex items-center gap-3">
        <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Categoria
        </span>
        <div className="flex flex-wrap gap-1.5">
          {categoriesLoading && (
            <>
              <Skeleton className="h-7 w-20 rounded-full" />
              <Skeleton className="h-7 w-24 rounded-full" />
              <Skeleton className="h-7 w-16 rounded-full" />
            </>
          )}
          {categories?.items.map((cat) => {
            const active = selectedCategories.has(cat.id);
            const colors = getCategoryColors(cat.outlookColor);
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => toggleCategory(cat.id)}
                aria-pressed={active}
                className={cn(
                  "inline-flex h-7 items-center rounded-full border px-3 text-xs font-medium transition-all duration-150",
                  active
                    ? "shadow-sm"
                    : "opacity-60 hover:opacity-90",
                )}
                style={
                  active
                    ? {
                        backgroundColor: colors.background,
                        color: colors.foreground,
                        borderColor: colors.border,
                        outline: `2px solid ${colors.background}`,
                        outlineOffset: "2px",
                      }
                    : {
                        backgroundColor: `${colors.background}22`,
                        color: colors.background,
                        borderColor: `${colors.background}55`,
                      }
                }
              >
                {cat.name}
              </button>
            );
          })}
          {!categoriesLoading && (
            <button
              type="button"
              onClick={toggleUncategorized}
              aria-pressed={filters.uncategorized === true}
              className={cn(
                "inline-flex h-7 items-center gap-1 rounded-full border px-3 text-xs font-medium transition-all duration-150",
                filters.uncategorized === true
                  ? "border-muted-foreground/40 bg-muted text-foreground shadow-sm"
                  : "border-dashed border-muted-foreground/30 text-muted-foreground opacity-60 hover:opacity-90",
              )}
            >
              <Tag className="h-3 w-3" />
              Nessuna categoria
            </button>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="hidden h-5 w-px bg-border sm:block" aria-hidden />

      {/* Priority */}
      <div className="flex items-center gap-2.5">
        <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Priorità
        </span>
        <div
          className="inline-flex items-center gap-0.5"
          role="group"
          aria-label="Priorità"
        >
          {PRIORITY_STARS.map((star) => {
            // Fill all stars up to and including the selected value
            const filled =
              filters.priority !== undefined && star <= filters.priority;
            const selected = filters.priority === star;
            return (
              <button
                key={star}
                type="button"
                onClick={() => handlePriority(star)}
                aria-pressed={selected}
                aria-label={`Priorità ${star}`}
                className={cn(
                  "rounded p-0.5 transition-all duration-100 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  filled ? "text-amber-400" : "text-muted-foreground/25 hover:text-amber-300",
                )}
              >
                <Star
                  className={cn("h-4 w-4", filled && "fill-amber-400")}
                  strokeWidth={1.5}
                />
              </button>
            );
          })}
        </div>
      </div>

      {/* Divider */}
      <div className="hidden h-5 w-px bg-border sm:block" aria-hidden />

      {/* Hide multi-day */}
      <div className="flex items-center gap-2">
        <CalendarX2 className="h-4 w-4 shrink-0 text-muted-foreground" />
        <label
          className={`inline-flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1 text-xs transition-colors ${
            filters.hideMultiDay
              ? "border-primary bg-primary/5 font-medium"
              : "border-input text-muted-foreground hover:bg-muted"
          }`}
        >
          <input
            type="checkbox"
            className="h-3 w-3 accent-primary"
            checked={filters.hideMultiDay === true}
            onChange={(e) =>
              onChange({ hideMultiDay: e.target.checked ? true : undefined })
            }
          />
          Nascondi multi-giorno
        </label>
      </div>

      {/* Clear */}
      {!isDefault && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="ml-auto h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
          Pulisci
        </Button>
      )}
    </div>
  );
}
